// src/components/shared/VoicePlayer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, X, RotateCcw, Mic } from 'lucide-react';

export const VoicePlayer = ({ 
  voice,
  title = 'Audio',
  position = 'top-right',
  autoPlay = false,
  onClose
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);
  
  const audioRef = useRef(null);
  
  const positionClasses = {
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
  };

  useEffect(() => {
    const attemptAutoPlay = async () => {
      if (autoPlay && audioRef.current) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          setAutoPlayBlocked(false);
        } catch (error) {
          setAutoPlayBlocked(true);
          setIsPlaying(false);
        }
      }
    };

    attemptAutoPlay();
  }, [autoPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      const percent = (audio.currentTime / audio.duration) * 100;
      setProgress(percent);
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!voice?.url) return null;

  return (
    <div className={`fixed ${positionClasses[position]} z-50 w-80 animate-in slide-in-from-right duration-300`}>
      <div className="bg-white rounded-xl shadow-2xl border-2 border-purple-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <Volume2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
              {voice.speaker?.name && (
                <p className="text-xs text-gray-500">{voice.speaker.name}</p>
              )}
            </div>
          </div>
          {onClose && (
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-4 space-y-3">
          {autoPlayBlocked && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-purple-800 text-center">
                🎵 Click play to start audio
              </p>
            </div>
          )}

          <button
            onClick={togglePlay}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {isPlaying ? (
              <>
                <Pause className="w-5 h-5" />
                Pause
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Play
              </>
            )}
          </button>

          <div className="space-y-1">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer group">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300 group-hover:from-purple-500 group-hover:to-pink-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>{formatTime(currentTime)}</span>
              <button
                onClick={handleRestart}
                className="hover:text-purple-600 transition-colors"
                title="Restart"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
              <span>{formatTime(voice.duration || 0)}</span>
            </div>
          </div>
        </div>

        <audio 
          ref={audioRef} 
          src={voice.url} 
          preload="metadata"
        />
      </div>
    </div>
  );
};